import {
  ThemeEvents,
  CartAddEvent
} from '@theme/events';

class Free2mlSample {

  // ============================================
  // 60ML → 2ML SAMPLE MAPPING
  // ============================================

  #sampleMap = {
    '52017840750876': '54406841991452', // Cherry Love Extreme → 2ml Cherry Love Extreme
    '52016257466652': '54406840254748', // Bohemian Water → 2ml Bohemian Water
    '52017948655900': '54406847070492', // Fantasma Overglow → 2ml Fantasma Overglow
    '52015290122524': '54406849560860', // Pistachio Voodoo Child → 2ml Pistachio Voodoo Child
    '52017896096028': '54406844416284', // Cashmere Show Pony → 2ml Cashmere Show Pony
    '53620845478172': '54406848413980'  // Lost In Vanillatopia → 2ml Lost In Vanillatopia
  };

  #busy = false;
  #syncTimer = null;

  constructor() {

    console.log(
      '[free-2ml-sample] Initialized'
    );

    // Shopify theme cart updates
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate
    );

    // Initial sync
    this.#scheduleSync(300);

    /*
     * Some cart components update the cart first
     * and dispatch their event slightly later.
     *
     * This catches those cases as well.
     */
    document.addEventListener(
      'click',
      this.#onDocumentClick,
      true
    );
  }

  // ============================================
  // CART UPDATE EVENT
  // ============================================

  #onCartUpdate = () => {

    console.log(
      '[free-2ml-sample] Cart update detected'
    );

    this.#scheduleSync(500);
  };

  // ============================================
  // DETECT CART REMOVE / QUANTITY BUTTON
  // ============================================

  #onDocumentClick = (event) => {

    const target =
      event.target.closest(
        [
          '[name="remove"]',
          '[data-cart-remove]',
          '[data-remove]',
          '[data-quantity-minus]',
          'button.quantity-minus',
          'button[name="minus"]',
          'cart-remove-button'
        ].join(',')
      );

    if (!target) {
      return;
    }

    console.log(
      '[free-2ml-sample] Cart quantity/remove clicked'
    );

    /*
     * Wait until Shopify finishes its cart request,
     * then check the actual cart.
     */
    this.#scheduleSync(700);
  };

  // ============================================
  // SCHEDULE SYNC
  // ============================================

  #scheduleSync(delay = 300) {

    clearTimeout(
      this.#syncTimer
    );

    this.#syncTimer =
      setTimeout(
        () => {
          this.#syncCart();
        },
        delay
      );
  }

  // ============================================
  // MAIN SYNC
  // ============================================

  async #syncCart() {

    if (this.#busy) {
      return;
    }

    try {

      const cart =
        await this.#getCart();

      if (!cart?.items) {
        return;
      }

      /*
       * Build a list of all parent 60ml products
       * currently in the cart.
       */
      const parentItems =
        cart.items.filter(
          (item) =>
            Object.prototype.hasOwnProperty.call(
              this.#sampleMap,
              String(item.variant_id)
            )
        );

      /*
       * All sample variant IDs.
       */
      const sampleVariantIds =
        new Set(
          Object.values(
            this.#sampleMap
          ).map(String)
        );

      /*
       * All sample products currently in cart.
       */
      const sampleItems =
        cart.items.filter(
          (item) =>
            sampleVariantIds.has(
              String(item.variant_id)
            )
        );

      console.log(
        '[free-2ml-sample] Cart status:',
        {
          parentItems: parentItems.map(
            (item) => ({
              variantId: item.variant_id,
              quantity: item.quantity
            })
          ),

          sampleItems: sampleItems.map(
            (item) => ({
              variantId: item.variant_id,
              quantity: item.quantity
            })
          )
        }
      );

      /*
       * Create the desired sample quantities.
       *
       * Every 60ml product controls its own
       * matching 2ml sample.
       */
      const desiredSamples = new Map();

      parentItems.forEach(
        (parentItem) => {

          const parentVariantId =
            String(
              parentItem.variant_id
            );

          const sampleVariantId =
            this.#sampleMap[
              parentVariantId
            ];

          if (!sampleVariantId) {
            return;
          }

          /*
           * One sample for each parent quantity.
           *
           * Example:
           * 60ml quantity 1 → sample quantity 1
           * 60ml quantity 2 → sample quantity 2
           */
          desiredSamples.set(
            String(sampleVariantId),
            Number(parentItem.quantity)
          );
        }
      );

      // ============================================
      // ADD / UPDATE REQUIRED SAMPLES
      // ============================================

      for (
        const [
          sampleVariantId,
          desiredQuantity
        ]
        of desiredSamples
      ) {

        const existingSamples =
          sampleItems.filter(
            (item) =>
              String(item.variant_id) ===
              sampleVariantId
          );

        const currentQuantity =
          existingSamples.reduce(
            (total, item) =>
              total +
              Number(item.quantity),
            0
          );

        /*
         * Sample does not exist.
         */
        if (
          currentQuantity === 0 &&
          desiredQuantity > 0
        ) {

          console.log(
            '[free-2ml-sample] Adding sample:',
            sampleVariantId
          );

          await this.#addSample(
            sampleVariantId,
            desiredQuantity
          );

          /*
           * Re-fetch cart after modification.
           */
          return;
        }

        /*
         * Sample quantity is different
         * from parent quantity.
         */
        if (
          currentQuantity !==
          desiredQuantity
        ) {

          console.log(
            '[free-2ml-sample] Syncing sample quantity:',
            {
              sampleVariantId,
              currentQuantity,
              desiredQuantity
            }
          );

          await this.#setSampleQuantity(
            existingSamples,
            desiredQuantity
          );

          return;
        }
      }

      // ============================================
      // REMOVE SAMPLES WHOSE 60ML IS GONE
      // ============================================

      const samplesToRemove =
        sampleItems.filter(
          (sampleItem) =>
            !desiredSamples.has(
              String(
                sampleItem.variant_id
              )
            )
        );

      if (
        samplesToRemove.length
      ) {

        console.log(
          '[free-2ml-sample] 60ml removed → removing matching samples:',
          samplesToRemove.map(
            (item) =>
              item.variant_id
          )
        );

        await this.#removeSamples(
          samplesToRemove
        );

        return;
      }

      console.log(
        '[free-2ml-sample] Cart already synchronized'
      );

    } catch (error) {

      console.error(
        '[free-2ml-sample] Sync error:',
        error
      );
    }
  }

  // ============================================
  // ADD SAMPLE
  // ============================================

  async #addSample(
    variantId,
    quantity = 1
  ) {

    if (this.#busy) {
      return;
    }

    this.#busy = true;

    try {

      const response =
        await fetch(
          '/cart/add.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body:
              JSON.stringify({
                items: [
                  {
                    id:
                      variantId,

                    quantity:
                      quantity
                  }
                ]
              })
          }
        );

      if (!response.ok) {

        const error =
          await response
            .json()
            .catch(
              () => ({})
            );

        throw new Error(
          error.description ||
          'Could not add 2ml sample.'
        );
      }

      const cart =
        await this.#getCart();

      console.log(
        '[free-2ml-sample] ✅ 2ml sample added:',
        variantId
      );

      this.#notifyTheme(
        cart,
        variantId
      );

    } catch (error) {

      console.error(
        '[free-2ml-sample] Add sample error:',
        error
      );

    } finally {

      this.#busy = false;
    }
  }

  // ============================================
  // REMOVE SAMPLE
  // ============================================

  async #removeSamples(
    sampleItems
  ) {

    if (this.#busy) {
      return;
    }

    this.#busy = true;

    try {

      const updates = {};

      sampleItems.forEach(
        (item) => {

          updates[
            item.key
          ] = 0;
        }
      );

      console.log(
        '[free-2ml-sample] Removing samples:',
        updates
      );

      const response =
        await fetch(
          '/cart/update.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body:
              JSON.stringify({
                updates
              })
          }
        );

      if (!response.ok) {

        const error =
          await response
            .json()
            .catch(
              () => ({})
            );

        throw new Error(
          error.description ||
          'Could not remove 2ml samples.'
        );
      }

      const cart =
        await response.json();

      console.log(
        '[free-2ml-sample] ✅ 2ml samples removed'
      );

      this.#notifyTheme(
        cart
      );

    } catch (error) {

      console.error(
        '[free-2ml-sample] Remove sample error:',
        error
      );

    } finally {

      this.#busy = false;
    }
  }

  // ============================================
  // SET SAMPLE QUANTITY
  // ============================================

  async #setSampleQuantity(
    sampleItems,
    quantity
  ) {

    if (this.#busy) {
      return;
    }

    this.#busy = true;

    try {

      const updates = {};

      /*
       * If there are duplicate sample lines,
       * keep the first one and remove duplicates.
       */
      sampleItems.forEach(
        (item, index) => {

          if (index === 0) {

            updates[
              item.key
            ] = quantity;

          } else {

            updates[
              item.key
            ] = 0;
          }
        }
      );

      const response =
        await fetch(
          '/cart/update.js',
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body:
              JSON.stringify({
                updates
              })
          }
        );

      if (!response.ok) {

        throw new Error(
          'Could not update sample quantity.'
        );
      }

      const cart =
        await response.json();

      console.log(
        '[free-2ml-sample] ✅ Sample quantity synchronized'
      );

      this.#notifyTheme(
        cart
      );

    } catch (error) {

      console.error(
        '[free-2ml-sample] Quantity error:',
        error
      );

    } finally {

      this.#busy = false;
    }
  }

  // ============================================
  // FETCH CART
  // ============================================

  async #getCart() {

    const response =
      await fetch(
        '/cart.js',
        {
          cache:
            'no-store',

          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {

      throw new Error(
        'Could not fetch cart.'
      );
    }

    return response.json();
  }

  // ============================================
  // UPDATE THEME CART
  // ============================================

  #notifyTheme(
    cart,
    variantId = ''
  ) {

    try {

      document.dispatchEvent(
        new CartAddEvent(
          cart,
          'free-2ml-sample',
          {
            source:
              'free-2ml-sample',

            didError:
              false,

            variantId:
              String(
                variantId
              ),

            itemCount:
              cart.item_count,

            sections: {}
          }
        )
      );

      /*
       * Give Shopify cart UI time to update,
       * then verify everything again.
       */
      this.#scheduleSync(
        500
      );

    } catch (error) {

      console.error(
        '[free-2ml-sample] Theme update error:',
        error
      );
    }
  }
}

// ============================================
// INITIALIZE ONCE
// ============================================

if (
  !window.__free2mlSampleInitialized
) {

  window.__free2mlSampleInitialized =
    true;

  new Free2mlSample();
}